// Copyright ©️ 2026 eVolve MEP, LLC

import React, { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import {
  type MessageId,
  type UpdateMessageBody,
  type MessageDetails,
  type Message,
  useEvolveDelete,
  useEvolveGet,
  useEvolvePaginatedGet,
  useEvolvePatch,
} from '@evolvemep/api-ts';
import { useLocation } from 'react-router';

import { MESSAGE_CENTER_TAB_FILTER_MAP } from 'app/MessageCenter/MessageCenterPage';
import { MESSAGE_PRIORITIES } from 'constants/globalConstants';
import { getAllDataFromFetcher } from 'helpers/getAllDataFromFetcher';
import { isNotNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useMaybeUser } from 'hooks/user/useMaybeUser';

type MessagesContextType = {
  inMessageCenter: boolean;
  deleteMessage: (messageId: MessageId) => Promise<void>;
  updateMessage: (messageId: MessageId, status: UpdateMessageBody['status']) => Promise<void>;
  updating: boolean;
  markAllRead: (hasMessageType?: boolean) => Promise<void>;
  markingRead: boolean;
  getMessageDetails: (messageId: MessageId) => Promise<MessageDetails>;
  gettingMessageDetails: boolean;
  undismissedHighPriorityMessages: Message[];
  setUndismissedHighPriorityMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  dismissAllHighPriority: () => Promise<void>;
  dismissingAll: boolean;
  /** Total number of unread Messages, not including Activity */
  totalUnread: number;
  refreshUnreadCount: () => Promise<void>;
};

const MessagesContext = createContext<MessagesContextType | undefined>(undefined);

export const MessagesProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const [undismissedHighPriorityMessages, setUndismissedHighPriorityMessages] = useState<Message[]>([]);
  const { user } = useMaybeUser();

  const [totalUnread, setTotalUnread] = useState(0);
  const {
    entireCount: unreadFromNetwork,
    fetchNextPage,
    reset,
  } = useEvolvePaginatedGet('admin/message', {
    perPage: 1,
    defaultConfig: {
      params: {
        ...MESSAGE_CENTER_TAB_FILTER_MAP['Messages'],
        read: false,
      },
    },
  });
  const refreshUnreadCount = useCallback(async () => {
    reset();
    await fetchNextPage();
  }, [fetchNextPage, reset]);

  useEffect(() => {
    if (isNotNil(unreadFromNetwork)) setTotalUnread(unreadFromNetwork);
  }, [unreadFromNetwork]);

  useEffect(() => {
    if (isNotNil(user)) {
      void refreshUnreadCount();
      const interval = setInterval(() => refreshUnreadCount(), 1000 * 60 * 5);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [refreshUnreadCount, user]);

  const { fetchPage: getHighPriorityMessages } = useEvolvePaginatedGet('admin/message', {
    defaultConfig: {
      params: {
        messagePriorityId: MESSAGE_PRIORITIES.HIGH,
        status: 'Active',
      },
    },
  });
  const { apiCall: updateMessageCall, loading: updating } = useEvolvePatch('admin/message/:messageId');
  const { apiCall: deleteMessageCall, loading: deleting } = useEvolveDelete('admin/message/:messageId');
  const { apiCall: getMessageDetailsCall, loading: gettingMessageDetails } = useEvolveGet('admin/message/:messageId');
  const { apiCall: markAllReadCall, loading: markingRead } = useEvolvePatch('admin/message/readAll');
  const { apiCall: dismissAllCall, loading: dismissingAll } = useEvolvePatch('admin/message/dismissAll');

  const deleteMessage = useCallback(
    (messageId: MessageId) =>
      deleteMessageCall({ url: `admin/message/${messageId}` }).then(() => {
        void refreshUnreadCount();
      }),
    [deleteMessageCall, refreshUnreadCount],
  );

  const updateMessage = useCallback(
    (messageId: MessageId, status: UpdateMessageBody['status']) =>
      updateMessageCall({ status }, { url: `admin/message/${messageId}` }).then(() => {
        void refreshUnreadCount();
        // If our message was high priority,
        // remove it from the "undismissed" list
        setUndismissedHighPriorityMessages((messages) => {
          const index = messages.findIndex((m) => m.messageId === messageId);
          if (index < 0) return messages;
          return [...messages.slice(0, index), ...messages.slice(index + 1)];
        });
      }),
    [updateMessageCall, refreshUnreadCount],
  );

  const dismissAllHighPriority = useCallback(
    () =>
      dismissAllCall({}).then(() => {
        setUndismissedHighPriorityMessages([]);
        void refreshUnreadCount();
      }),
    [dismissAllCall, refreshUnreadCount],
  );

  const getMessageDetails = useCallback(
    (messageId: MessageId) => getMessageDetailsCall({ url: `admin/message/${messageId}` }),
    [getMessageDetailsCall],
  );

  const markAllRead = useCallback(
    (hasMessageType?: boolean) =>
      markAllReadCall({ hasMessageType }).then(() => {
        setUndismissedHighPriorityMessages([]);
        void refreshUnreadCount();
      }),
    [markAllReadCall, refreshUnreadCount],
  );

  useEffect(() => {
    if (isNotNil(user)) {
      void getAllDataFromFetcher(getHighPriorityMessages, {}).then(setUndismissedHighPriorityMessages);
    }
  }, [getHighPriorityMessages, user]);

  const inMessageCenter = useMemo(
    () =>
      pathname
        .substring(1, pathname.indexOf('/', 1) !== -1 ? pathname.indexOf('/', 1) : pathname.length)
        .toLocaleUpperCase() === 'MESSAGE',
    [pathname],
  );

  const messagesObj = useMemo<MessagesContextType>(
    () => ({
      inMessageCenter,
      deleteMessage,
      deleting,
      updateMessage,
      updating,
      dismissAllHighPriority,
      dismissingAll,
      markAllRead,
      markingRead,
      getMessageDetails,
      gettingMessageDetails,
      undismissedHighPriorityMessages,
      setUndismissedHighPriorityMessages,
      totalUnread,
      refreshUnreadCount,
    }),
    [
      inMessageCenter,
      deleteMessage,
      deleting,
      updateMessage,
      updating,
      dismissAllHighPriority,
      dismissingAll,
      markAllRead,
      markingRead,
      getMessageDetails,
      gettingMessageDetails,
      undismissedHighPriorityMessages,
      totalUnread,
      refreshUnreadCount,
    ],
  );
  return <MessagesContext value={messagesObj}>{children}</MessagesContext>;
};

export const useMessages = () => useGeneralContext(MessagesContext, 'Messages');
